import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'

import { HOME_CONTENT } from '../defaults'

import { BlinkingCursor } from './BlinkingCursor'

// ---------------------------------------------------------------------------
// Public Component
// ---------------------------------------------------------------------------

export const TerminalTypewriter: FC = () => {
  const { text, showCursor } = useTerminalTypewriter()

  return (
    <Box sx={sectionSx}>
      <Box sx={terminalContainerSx} aria-hidden="true">
        <Box sx={lineRowSx}>
          <Typography component="span" sx={promptSx}>
            &gt;
          </Typography>
          <Typography component="span" sx={lineTextSx}>
            {text}
          </Typography>
          {showCursor && <BlinkingCursor color={MATRIX_GREEN} fontSize="inherit" />}
        </Box>
      </Box>
      {/* Static accessible summary — OUTSIDE aria-hidden so screen readers can reach it */}
      <Typography sx={visuallyHiddenSx}>{HOME_CONTENT.terminal.accessibilitySummary}</Typography>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Phase = 'typing' | 'pause' | 'deleting'

interface TypewriterState {
  readonly messageIndex: number
  readonly charIndex: number
  readonly phase: Phase
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MATRIX_GREEN = '#00ff41'

const TYPING_SPEED_MS = 65
const DELETING_SPEED_MS = 35
const PAUSE_MS = 1200
const FINAL_PAUSE_MS = 2500

const messages = HOME_CONTENT.terminal.messages

const assertMessage = (index: number): string => {
  const msg = messages[index]
  if (msg === undefined) throw new Error(`[TerminalTypewriter] invalid messageIndex: ${index}`)
  return msg
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const INITIAL_STATE: TypewriterState = {
  messageIndex: 0,
  charIndex: 0,
  phase: 'typing',
}

const useTerminalTypewriter = () => {
  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const [state, setState] = useState<TypewriterState>(INITIAL_STATE)

  useEffect(() => {
    if (prefersReducedMotion.current) return

    const delay = computeDelay(state)
    const timeoutId = setTimeout(() => {
      setState(computeNextState)
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [state])

  if (prefersReducedMotion.current) {
    return { text: assertMessage(messages.length - 1), showCursor: false }
  }

  const fullMessage = assertMessage(state.messageIndex)
  return {
    text: fullMessage.slice(0, state.charIndex),
    showCursor: true,
  }
}

// ---------------------------------------------------------------------------
// State Machine — Pure Functions
// ---------------------------------------------------------------------------

const computeNextState = (current: TypewriterState): TypewriterState => {
  const message = assertMessage(current.messageIndex)

  switch (current.phase) {
    case 'typing': {
      if (current.charIndex < message.length) {
        return { ...current, charIndex: current.charIndex + 1 }
      }
      return { ...current, phase: 'pause' }
    }

    case 'pause':
      return { ...current, phase: 'deleting' }

    case 'deleting': {
      if (current.charIndex > 0) {
        return { ...current, charIndex: current.charIndex - 1 }
      }
      // Advance to next message, loop at end
      const nextIndex = (current.messageIndex + 1) % messages.length
      return { messageIndex: nextIndex, charIndex: 0, phase: 'typing' }
    }
  }
}

const computeDelay = (state: TypewriterState): number => {
  switch (state.phase) {
    case 'typing':
      return TYPING_SPEED_MS
    case 'pause':
      // Longer pause on the final message before looping
      return state.messageIndex === messages.length - 1 ? FINAL_PAUSE_MS : PAUSE_MS
    case 'deleting':
      return DELETING_SPEED_MS
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 2, md: 3 },
  px: { xs: 2, md: 4 },
}

const TEXT_GLOW = '0 0 8px rgba(0, 255, 65, 0.4), 0 0 16px rgba(0, 255, 65, 0.15)'

const terminalContainerSx: SxProps<Theme> = (theme) => ({
  width: '100%',
  maxWidth: 720,
  mx: 'auto',
  px: { xs: 2, md: 3 },
  py: 2,
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(62, 39, 35, 0.08)',
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(62, 39, 35, 0.03)',
  display: 'flex',
  alignItems: 'center',
  minHeight: 48,
})

const lineRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
}

const promptSx: SxProps<Theme> = {
  color: MATRIX_GREEN,
  fontSize: { xs: '0.85rem', md: '1.15rem' },
  fontWeight: 600,
  opacity: 0.8,
  textShadow: TEXT_GLOW,
  flexShrink: 0,
}

const lineTextSx: SxProps<Theme> = {
  color: MATRIX_GREEN,
  fontSize: { xs: '0.85rem', md: '1.15rem' },
  fontWeight: 500,
  textShadow: TEXT_GLOW,
  whiteSpace: { xs: 'normal', md: 'nowrap' },
  wordBreak: { xs: 'break-word', md: 'normal' },
}

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
}
